<template>
  <div :class="[props.class, $style.multi_check_box]">
    <label
      v-for="(option, index) in options"
      :key="index"
      :class="[$style.check_box_item, { [$style.disabled]: option.disabled }]"
    >
      <input
        :id="`${name}_${index}`"
        v-model="items"
        :class="$style.check_box"
        type="checkbox"
        :value="option.value"
        :disabled="option.disabled"
        @change="format()"
      />
      <span :key="option.key" :class="$style.check_box_text" :for="`${name}_${index}`">
        {{ option.key }}
      </span>
    </label>
    <label v-if="isRequiredTextForm" :class="$style.check_box_item">
      <input
        :id="`${name}_other`"
        v-model="items"
        :class="$style.check_box"
        type="checkbox"
        value="other"
        @change="format()"
      />
      <span :class="$style.check_box_text" :for="`${name}_other`">その他</span>
    </label>
    <AtomsConsumerInput
      v-if="isRequiredTextForm && isShowTextForm"
      v-model="other"
      type="text"
      block
      placeholder="宜しければ具体的にご記入ください"
      @input="format()"
    />
  </div>
</template>

<script lang="ts" setup>
type Props = {
  value: string[]
  list: string[] | { key: string; value: string; disabled?: boolean }[]
  name: string
  class?: string
  isRequiredTextForm?: boolean
  limit?: number
}

const props = withDefaults(defineProps<Props>(), {
  value: () => [],
  list: () => [],
  name: "",
  class: "",
  isRequiredTextForm: true,
  limit: 999,
})

const emit = defineEmits<{
  (e: "confirm"): void
  (e: "update:value", visible: string[]): void
}>()

const items = ref<string[]>(props.value)
const other = ref<string>("")
const isShowTextForm = ref(false)
const options = computed<
  {
    key: string
    value: string
    disabled?: boolean
  }[]
>(() =>
  props.list.map((el) => {
    if (typeof el === "string") {
      return {
        key: el,
        value: el,
      }
    } else {
      return el
    }
  }),
)

const format = () => {
  items.value = items.value.slice(-props.limit)

  isShowTextForm.value = items.value.includes("other")

  const outputValue = items.value.map((_item) => {
    return _item === "other" ? other.value : _item
  })

  emit("update:value", outputValue)
}

watch(
  () => props.value,
  (value) => {
    // その他が選択可能な場合、props.value が変動 -> 初期化されてしまうのを防ぐ
    if (!props.isRequiredTextForm) {
      items.value = value
    }
  },
)
</script>

<style scoped module lang="scss">
.multi_check_box {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &.two_column {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    .check_box_item {
      width: calc(50% - 0.625rem);
    }
  }

  // AtomConsumerCheckbox のスタイルと同じ
  .check_box_item {
    display: flex;
    align-items: center;
    cursor: pointer;
    width: fit-content;
    gap: 0.5rem;
    input[type="checkbox"] {
      // デフォルトのスタイルを消す
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      padding: 0;
      cursor: pointer;
      position: relative;
      width: 1rem;
      min-width: 1rem;
      height: 1rem;
      border: 1px solid;
      border-radius: 4px;
      border-color: $primary-20;
      bottom: 1px;

      &:checked {
        border-color: $primary;
        background-color: $primary;
      }

      // チェックマークを生成
      &:checked:before {
        position: absolute;
        top: 2px;
        left: 5px;
        transform: rotate(50deg);
        width: 0.25rem;
        height: 0.5rem;
        border-right: 2px solid $white;
        border-bottom: 2px solid $white;
        content: "";
      }

      &:disabled {
        cursor: not-allowed;
        border-color: $black-20;
        color: $black-20;
      }
    }

    &.disabled {
      pointer-events: none;
      cursor: not-allowed;
      color: $black-20;
    }

    .check_box_text {
      display: inline-block;
      padding: 0;
      text-align: left;
    }
  }
}
</style>
